<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="3" class="mb-1">
            <label>Неделя</label>
            <v-select
              v-model="filters.week_id"
              :options="weeks"
              label="name"
              :reduce="w => w.id"
              placeholder="Все недели"
            />
          </b-col>
          <b-col cols="12" md="3" class="mb-1">
            <label>Ведущий</label>
            <v-select
              v-model="filters.host"
              :options="hosts"
              placeholder="Все ведущие"
            />
          </b-col>
          <b-col cols="12" md="3" class="mb-1">
            <label>Статус</label>
            <b-form-select v-model="filters.status" :options="statusOptions" />
          </b-col>
          <b-col cols="12" md="3" class="mb-1">
            <label>Источник</label>
            <b-form-select v-model="filters.source" :options="sourceOptions" />
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" md="3" class="mb-1">
            <label>С</label>
            <flat-pickr v-model="filters.from" class="form-control" :config="datePickerConfig" placeholder="Не задано" />
          </b-col>
          <b-col cols="12" md="3" class="mb-1">
            <label>По</label>
            <flat-pickr v-model="filters.to" class="form-control" :config="datePickerConfig" placeholder="Не задано" />
          </b-col>
          <b-col cols="12" md="4" class="mb-1">
            <label>Поиск</label>
            <b-form-input v-model="filters.q" placeholder="Тема, пакет, ведущий..." @keyup.enter="applyFilters" />
          </b-col>
          <b-col cols="12" md="2" class="d-flex align-items-end mb-1">
            <b-button variant="primary" block @click="applyFilters">Применить</b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-overlay :show="isLoading" rounded="sm">
      <b-card no-body>
        <b-card-body>
          <vue-good-table
            mode="remote"
            :rows="games"
            :columns="columns"
            :total-rows="meta.total"
            :pagination-options="{ enabled: true, perPage, perPageDropdown: [25, 50, 100], setCurrentPage: page }"
            :sort-options="{ enabled: false }"
            @on-page-change="onPageChange"
            @on-per-page-change="onPerPageChange"
          >
            <template slot="emptystate">
              <div class="text-center text-muted py-2">Игры не найдены</div>
            </template>
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'scheduled_at'">
                {{ formatDateTime(props.row.scheduled_at) }}
                <small v-if="props.row.is_rescheduled" class="text-warning d-block">
                  факт {{ formatDateTime(props.row.actual_start_at) }}
                </small>
              </span>
              <span v-else-if="props.column.field === 'week_name'">{{ props.row.week_name || '—' }}</span>
              <span v-else-if="props.column.field === 'topic_name'">{{ props.row.topic_name || '—' }}</span>
              <span v-else-if="props.column.field === 'pack_name'">{{ props.row.pack_name || '—' }}</span>
              <span v-else-if="props.column.field === 'host_name'">
                {{ props.row.host_name || '—' }}
                <span v-if="props.row.is_host_changed" class="text-warning">
                  → {{ props.row.actual_host_name }}
                </span>
              </span>
              <span v-else-if="props.column.field === 'players_count'">
                {{ props.row.players_count != null ? props.row.players_count : '—' }}
              </span>
              <span v-else-if="props.column.field === 'payments_sum'">{{ formatRub(props.row.payments_sum) }}</span>
              <span v-else-if="props.column.field === 'status'">
                <plan-fact-status-badge :game="props.row" />
              </span>
              <span v-else-if="props.column.field === 'task_url'">
                <b-link v-if="props.row.task_url" :href="props.row.task_url" target="_blank" rel="noopener noreferrer">
                  задача
                </b-link>
                <span v-else class="text-muted">—</span>
              </span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BFormSelect, BFormInput, BButton, BOverlay, BLink,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import { ref, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import gameStoreModule from '@/views/game/gameStoreModule'
import PlanFactStatusBadge from '@/views/game/components/PlanFactStatusBadge.vue'
import { STATUS_OPTIONS, SOURCE_OPTIONS, formatDateTime } from '@/views/game/planFact'
import { formatRub } from '@/views/finance/utils'

const STORE_MODULE_NAME = 'app-games'

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormSelect,
    BFormInput,
    BButton,
    BOverlay,
    BLink,
    VueGoodTable,
    vSelect,
    flatPickr,
    PlanFactStatusBadge,
  },
  setup() {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, gameStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const toast = useToast()
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })

    const datePickerConfig = { dateFormat: 'Y-m-d', altInput: true, altFormat: 'd.m.Y' }

    const statusOptions = [{ value: '', text: 'Все статусы' }, ...STATUS_OPTIONS]
    const sourceOptions = [{ value: '', text: 'Все источники' }, ...SOURCE_OPTIONS]

    const weeks = ref([])
    const hosts = ref([])
    const games = ref([])
    const meta = ref({
      total: 0, per_page: 25, current_page: 1, last_page: 1,
    })
    const isLoading = ref(false)
    const page = ref(1)
    const perPage = ref(25)

    const filters = ref({
      week_id: null,
      host: null,
      status: '',
      source: '',
      from: '',
      to: '',
      q: '',
    })

    const columns = [
      { label: 'Дата/время', field: 'scheduled_at' },
      { label: 'Неделя', field: 'week_name' },
      { label: 'Тема', field: 'topic_name' },
      { label: 'Пакет', field: 'pack_name' },
      { label: 'Ведущий', field: 'host_name' },
      { label: 'Игроков', field: 'players_count' },
      { label: 'Оплата', field: 'payments_sum' },
      { label: 'Статус', field: 'status' },
      { label: 'Задача', field: 'task_url' },
    ]

    const fetchGames = () => {
      isLoading.value = true
      const params = { page: page.value, per_page: perPage.value }
      if (filters.value.week_id) params.week_id = filters.value.week_id
      if (filters.value.host) params.host = filters.value.host
      if (filters.value.status) params.status = filters.value.status
      if (filters.value.source) params.source = filters.value.source
      if (filters.value.from) params.from = filters.value.from
      if (filters.value.to) params.to = filters.value.to
      if (filters.value.q) params.q = filters.value.q

      store.dispatch('app-games/fetchPlanfixGames', params)
        .then(response => {
          games.value = response.data.data || []
          meta.value = response.data.meta || meta.value
        })
        .catch(error => showError((error.response && error.response.data && error.response.data.message) || 'Ошибка загрузки игр'))
        .finally(() => {
          isLoading.value = false
        })
    }

    const applyFilters = () => {
      page.value = 1
      fetchGames()
    }

    const onPageChange = params => {
      page.value = params.currentPage
      fetchGames()
    }

    const onPerPageChange = params => {
      perPage.value = params.currentPerPage
      page.value = 1
      fetchGames()
    }

    store.dispatch('app-games/fetchGameWeeks')
      .then(response => {
        weeks.value = response.data.data
      })
      .catch(() => showError('Ошибка загрузки недель'))

    store.dispatch('app-games/fetchPlanfixGameHosts')
      .then(response => {
        hosts.value = response.data.data
      })
      .catch(() => showError('Ошибка загрузки ведущих'))

    fetchGames()

    return {
      filters,
      statusOptions,
      sourceOptions,
      datePickerConfig,
      weeks,
      hosts,
      games,
      meta,
      isLoading,
      page,
      perPage,
      columns,
      applyFilters,
      onPageChange,
      onPerPageChange,
      formatDateTime,
      formatRub,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
